import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
    Button,
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
    IconButton,
} from "@mui/material"

import { container } from "../../infrastructure/container"
import { Logger } from "../../infrastructure/services/logger"
import { SessionService } from "../../infrastructure/services/session"
import { SupportUserService } from "../../infrastructure/services/api/supportUser"
import CreateSupportUserForm from "../../presentation/component/CreateSupportUserForm"
import { useSearch } from "../layouts/SideBarLayout"
import { SupportUserBriefResponse } from "../../infrastructure/models/transport/response/supportUserBrief"
import { CreateSupportUsers } from "../../infrastructure/models/transport/request/createSupportUsers"
import { SupportRole } from "../../infrastructure/models/common/supportrole"
import { Pencil, Trash2 } from "lucide-react"
import { COLORS } from "../../presentation/constants/colors"
import UpdateSupportUserForm from "./UpdateSupportUserForm"
import { UpdateSupportUserRequest } from "../../infrastructure/models/transport/request/UpdateSupportUserRequest"
/**
 * SupportUser Page — lists existing support‑users and allows creation of new ones.
 */
const SupportUser: React.FC = () => {
    // IoC / DI services
    const sessionService = container.resolve<SessionService>("sessionService")
    const currentUser = sessionService.user // or getLoggedInUser()
    const currentUserRole = currentUser?.role

    const supportUserService = container.resolve<SupportUserService>(
        "supportUserService"
    )
    const logger = container.resolve<Logger>("logger")

    // Router + search
    const navigate = useNavigate()
    const { searchQuery } = useSearch()

    // Logged‑in user (memoised)
    const user = useMemo(() => sessionService.user, [sessionService.user])

    // Support‑user list — load from session first
    const [supportUsers, setSupportUsers] = useState<SupportUserBriefResponse[]>(
        () => sessionService.supportUserBrief
    )

    // UI state
    const [dialogOpen, setDialogOpen] = useState(false)
    const [pageLoading, setPageLoading] = useState(false)
    const [apiLoading, setApiLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [selectedUserEmail, setSelectedUserEmail] = useState<string | null>(null)
    const [updateSuccessOpen, setUpdateSuccessOpen] = useState(false)
    const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false)
    const [editMode, setEditMode] = useState(false)
    const [userToEdit, setUserToEdit] = useState<SupportUserBriefResponse | null>(null)
    const canEditRoles = [SupportRole.SUPERADMIN, SupportRole.ADMIN, SupportRole.MANAGER]
    const canDeleteRoles = [SupportRole.SUPERADMIN]

    // ----------------------------
    // Initial data fetch
    // ----------------------------
    useEffect(() => {
        if (!user) {
            navigate("/login")
            return
        }

        const fetchSupportUsers = async () => {
            // If we already have data in session, skip fetch.
            if (supportUsers.length > 0) return
            sessionService.setSupportUserBrief([])
            try {
                setPageLoading(true)
                const fetched = await supportUserService.getSupportUsers()
                sessionService.setSupportUserBrief(fetched)
                setSupportUsers(fetched)
            } catch (err) {
                logger.error("Failed to fetch support users", err)
                setError("Unable to fetch support users. Please try again later.")
            } finally {
                setPageLoading(false)
            }
        }

        fetchSupportUsers()
    }, [])

    // ----------------------------
    // Handlers
    // ----------------------------
    const handleCreateSupportUser = async (data: CreateSupportUsers) => {
        setApiLoading(true)
        try {
            const response = await supportUserService.createSupportUsers(data)

            const brief: SupportUserBriefResponse = {
                id: response.email,
                name: `${response.firstname} ${response.lastname ?? ""}`.trim(),
                email: response.email,
                role: response.role,
                franchises: Array.isArray(response.franchises)
                    ? response.franchises.map((f: any) => {
                        if (typeof f === "string") {
                            const full = sessionService.franchises?.find(
                                fr => fr.franchiseId === f
                            )
                            return {
                                franchiseId: f,
                                name: full?.name ?? "Unknown",
                            }
                        }
                        return {
                            franchiseId: f.franchiseId,
                            name: f.name ?? "Unknown",
                        }
                    })
                    : [],
                isActive: response.isActive,
            }
            setSupportUsers(prev => [...prev, brief])
            setDialogOpen(false)

        } catch (error: any) {

            const apiData = error?.response?.data || error
            const errorCode = apiData?.code
            const message = apiData?.message

            // DUPLICATE SUPPORT USER
            if (errorCode === "E01") {
                setError("Email_Already_Registered")
                return
            }

            // OTHER BACKEND ERRORS
            if (message) {
                setError(message)
                return
            }

            // FALLBACK
            setError("Something went wrong. Please try again.")
        } finally {
            setApiLoading(false)
        }
    }

    const handleUpdateSupportUser = async (data: UpdateSupportUserRequest) => {
        setApiLoading(true)
        try {
            await supportUserService.updateSupportUser(data)

            // Fetch the latest support users
            const refreshed = await supportUserService.getSupportUsers()
            setSupportUsers(refreshed)
            sessionService.setSupportUserBrief(refreshed)

            setDialogOpen(false)
            setEditMode(false)
            setUserToEdit(null)
            setUpdateSuccessOpen(true)
        } catch (err) {
            logger.error("Failed to update support user", err)
            setError("Failed to update support user. Please try again.")
        } finally {
            setApiLoading(false)
        }
    }

    const displayedUsersSearch = useMemo(() => {
        const q = searchQuery.toLowerCase()
        return supportUsers.filter(({ name, email, role }) =>
            `${name} ${email}, ${SupportRole[role]}`.toLowerCase().includes(q)
        )
    }, [supportUsers, searchQuery])

    // Deleting Support Users
    const handleConfirmDelete = (email: string) => {
        setSelectedUserEmail(email)
        setDeleteDialogOpen(true)
    }

    const handleDeleteUser = async () => {
        if (!selectedUserEmail) return
        try {
            setApiLoading(true)
            await supportUserService.deleteSupportUsers(selectedUserEmail)

            const updated = supportUsers.filter(user => user.email !== selectedUserEmail)
            setSupportUsers(updated)
            sessionService.setSupportUserBrief(updated)

            setDeleteDialogOpen(false)
            setSelectedUserEmail(null)

            // Show success popup
            setDeleteSuccessOpen(true)
        } catch (err) {
            logger.error("Failed to delete support user", err)
            setError("Failed to delete user. Please try again.")
        } finally {
            setApiLoading(false)
        }
    }

    useEffect(() => {
        if (updateSuccessOpen || deleteSuccessOpen) {
            const timer = setTimeout(() => {
                setUpdateSuccessOpen(false)
                setDeleteSuccessOpen(false)
            }, 2000)
            return () => clearTimeout(timer)
        }
    }, [updateSuccessOpen, deleteSuccessOpen])

    // ----------------------------
    // Render
    // ----------------------------
    if (pageLoading) {
        return (
            <div className="flex h-full w-full items-center justify-center">
                <CircularProgress />
            </div>
        )
    }

    return (
        <div className="flex flex-col p-10">
            <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: "gray.800" }}>
                    List of Support Users
                </Typography>

                <Button
                    variant="contained"
                    onClick={() => setDialogOpen(true)}
                    disabled={apiLoading}
                >
                    Create Support User
                </Button>
            </Box>

            {/* Empty‑state */}
            {displayedUsersSearch.length === 0 ? (
                <p className="text-center text-gray-500">No users available.</p>
            ) : (
                <div className="w-full h-[500px] overflow-hidden text-sm rounded-lg">
                    <div className="h-full overflow-y-auto scroll-smooth">
                        <table className="min-w-full table-fixed border-separate border-spacing-0">
                            {/* Fixed header */}
                            <thead className="sticky top-0 z-10 bg-gray-100">
                                <tr>
                                    <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">#</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Name</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Email</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Franchise Name</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Role</th>
                                    <th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Status</th>
                                    {currentUserRole !== SupportRole.SUPPORT && (<th className="px-4 py-2 text-left font-medium text-gray-900 uppercase">Action</th>
                                    )}</tr>
                            </thead>

                            {/* Scrollable tbody */}
                            <tbody className="bg-white divide-y divide-gray-200">
                                {displayedUsersSearch.map((u, idx) => (
                                    <tr key={u.id} className="hover:bg-gray-50 border-b border-gray-200">
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">{idx + 1}</td>
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">{u.name}</td>
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">{u.email}</td>
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">
                                            {u.franchises.map((f, i) => (
                                                <span key={f.franchiseId}>
                                                    {f.name}
                                                    {i < u.franchises.length - 1 && <>, <br /></>}
                                                </span>
                                            ))}
                                        </td>
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">{SupportRole[u.role]}</td>
                                        <td className="px-4 py-2 text-gray-900 whitespace-nowrap">
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Box
                                                    className="px-3 py-1 text-xs font-medium rounded-full text-white"
                                                    sx={{
                                                        backgroundColor: u.isActive ? COLORS.ACTIVE : COLORS.INACTIVE,
                                                    }}
                                                >
                                                    {u.isActive ? "Active" : "Inactive"}
                                                </Box>
                                            </Box>
                                        </td>
                                        {currentUserRole !== SupportRole.SUPPORT && (<td className="px-4 py-2 text-gray-900 whitespace-nowrap">
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                {/* === EDIT BUTTON LOGIC === */}
                                                {(
                                                    (currentUserRole === SupportRole.SUPERADMIN) ||
                                                    (currentUserRole === SupportRole.ADMIN &&
                                                        (u.role === SupportRole.MANAGER || u.role === SupportRole.SUPPORT)) ||
                                                    (currentUserRole === SupportRole.MANAGER &&
                                                        u.role === SupportRole.SUPPORT)
                                                ) && (
                                                        <IconButton
                                                            size="small"
                                                            sx={{
                                                                width: 28,
                                                                height: 28,
                                                                bgcolor: '#d4d4d4ad',
                                                                transition: 'background-color 0.2s',
                                                                borderRadius: '50%',
                                                                '&:hover': {
                                                                    bgcolor: '#d4d4d4',
                                                                }
                                                            }}
                                                            color="primary"
                                                            onClick={() => {
                                                                setEditMode(true)
                                                                setUserToEdit(u)
                                                                setDialogOpen(true)
                                                            }}
                                                        >
                                                            <Pencil style={{ fontSize: 12 }} />
                                                        </IconButton>
                                                    )}

                                                {/* === DELETE BUTTON LOGIC (Only visible for superadmin login) === */}
                                                {currentUserRole === SupportRole.SUPERADMIN && (
                                                    <IconButton
                                                        size="small"
                                                        sx={{
                                                            width: 28,
                                                            height: 28,
                                                            bgcolor: '#d4d4d4ad',
                                                            transition: 'background-color 0.2s',
                                                            borderRadius: '50%',
                                                            '&:hover': {
                                                                bgcolor: '#d4d4d4',
                                                            },
                                                        }}
                                                        color="error"
                                                        onClick={() => handleConfirmDelete(u.email)}
                                                    >
                                                        <Trash2 style={{ fontSize: 12 }} />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Dialog */}
            {currentUserRole !== SupportRole.SUPPORT && (
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                    <DialogTitle>{editMode ? "Update Support User" : "Create New Support User"}</DialogTitle>
                    <DialogContent>
                        {apiLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : editMode && userToEdit ? (
                            <UpdateSupportUserForm
                                defaultValues={{
                                    email: userToEdit.email,
                                    firstname: userToEdit.firstname ?? userToEdit.name.split(" ")[0] ?? "",
                                    lastname: userToEdit.lastname ?? userToEdit.name.split(" ")[1] ?? "",
                                    role: userToEdit.role,
                                    franchises: userToEdit.franchises,
                                    isActive: userToEdit.isActive,
                                    address: userToEdit.address ?? {
                                        street: "",
                                        city: "",
                                        state: "",
                                        postalCode: ""
                                    }
                                }}
                                isLoading={apiLoading}
                                onSubmit={handleUpdateSupportUser}
                                onCancel={() => {
                                    setDialogOpen(false)
                                    setEditMode(false)
                                    setUserToEdit(null)
                                }}
                            />

                        ) : (
                            <CreateSupportUserForm
                                onSubmit={handleCreateSupportUser}
                                currentUserRole={currentUserRole}
                                apiError={error}
                            />
                        )}
                    </DialogContent>

                    <DialogActions>
                        <Button
                            onClick={() => {
                                setDialogOpen(false)
                                setEditMode(false)
                                setUserToEdit(null)
                            }}
                            color="secondary"
                        >
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>

            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this support user?</Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">
                        Cancel
                    </Button>

                    <Button
                        onClick={handleDeleteUser}
                        color="error"
                        disabled={apiLoading}
                        variant="contained"
                    >
                        {apiLoading ? <CircularProgress size={20} /> : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteSuccessOpen}
                onClose={() => setDeleteSuccessOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "64px",
                            height: "64px",
                            borderRadius: "50%",
                            backgroundColor: "#4CAF50",
                            margin: "0 auto 16px auto",
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-8 h-8"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="white"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Support User Deleted Successfully.
                    </Typography>
                </DialogContent>
            </Dialog>

            <Dialog
                open={updateSuccessOpen}
                onClose={() => setUpdateSuccessOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogContent sx={{ textAlign: 'center', py: 4 }}>
                    <div
                        style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "64px",
                            height: "64px",
                            borderRadius: "50%",
                            backgroundColor: "#4CAF50",
                            margin: "0 auto 16px auto"
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-8 h-8"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="white"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                            />
                        </svg>
                    </div>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        Support User Updated Successfully.
                    </Typography>
                </DialogContent>
            </Dialog>

            {/* Error banner */}
            {error && (
                <Box sx={{ mt: 2 }} className="text-center text-red-600">
                    {error}
                </Box>
            )}
        </div>
    )
}

export default SupportUser